import { useEffect, useState } from 'react'
import { Document, Page, PDFViewer, StyleSheet, Text } from '@react-pdf/renderer'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdjustPageHeadEntities } from 'ui_kit_qmd'
import PatientChartsPrintedFriendlyHeader from './PatientChartsPrintedFriendlyHeader/PatientChartsPrintedFriendlyHeader'
import PatientChartsPrintedFriendlySection from './PatientChartsPrintedFriendlySection/PatientChartsPrintedFriendlySection'
import PatientChartsPrintedFriendlyFooter from './PatientChartsPrintedFriendlyFooter/PatientChartsPrintedFriendlyFooter'
import { dataSkeletonGenerator } from './helpers/dataSkeleton/dataSkeleton'
import { PATIENT_CHARTS_PAGE_ROUTE, QUICK_MD_TITLE_TEXT } from '../../utils/constants'
import { parseTimeZone } from '../../utils/common'
import { formatNameForPatientChartTitle } from '../patient-chart-page/helper'

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    padding: 32,
    flex: 1,
    justifyContent: 'flex-start',
  },
  reportSection: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  separateLine: {
    borderBottom: '1px solid #C2D5E2',
    flex: 1,
  },
  sectionTitle: {
    color: '#141414',
    textAlign: 'right',
    fontFamily: 'Helvetica',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '700',
    marginBottom: 16,
  },
  sectionItemTitle: {
    color: '#141414',
    textAlign: 'right',
    fontFamily: 'Helvetica',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: '700',
    marginBottom: 8,
  },
  sectionItem: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  sectionItemProperty: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    flexWrap: 'nowrap',
    color: '#767676',
    fontFamily: 'Helvetica',
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: '400',
    textAlign: 'left',
  },

  sectionItemPropertyValue: {
    color: '#141414',
  },
  pageNumberText: {
    color: '#141414',
    fontFamily: 'Helvetica',
    fontSize: 10,
    fontStyle: 'normal',
    fontWeight: '400',
    textAlign: 'right',
    left: 0,
    bottom: 0,
    right: 24,
  },
})

const PatientChartsPrintedFriendly = () => {
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientChartPrintedInfo = useSelector((state) => state.chartsReducer.patientChartPrintedInfo)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const profile = useSelector((state) => state.accountSettings.adminProfile)

  const { id } = useParams()
  const navigate = useNavigate()
  const [userTimeZone, setUserTimeZone] = useState(parseTimeZone(profile.TimeZone))
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)?.[id] || {}
  const patientDataById = patientChartPrintedInfo[id]

  const { FirstName, LastName } = patientInfo || {}
  const pageTitle = `${formatNameForPatientChartTitle(FirstName, LastName)} - PDF`
  const pageHeader = useAdjustPageHeadEntities({
    title: pageTitle,
  })
  useEffect(
    () => () => {
      document.title = QUICK_MD_TITLE_TEXT
    },
    [],
  )
  useEffect(() => {
    setUserTimeZone(parseTimeZone(profile.TimeZone))
  }, [profile])

  useEffect(() => {
    if (!patientDataById) {
      navigate(PATIENT_CHARTS_PAGE_ROUTE)
    }
  }, [navigate, patientDataById])

  const dataForPrintingSkeleton = dataSkeletonGenerator(patientDataById, userTimeZone)

  return (
    <div style={{ height: '100vh' }}>
      {pageHeader}
      <PDFViewer width='100%' height='100%'>
        <Document title='QuickMD Patient chart report'>
          <Page size='A4' style={styles.page}>
            <PatientChartsPrintedFriendlyHeader />
            {dataForPrintingSkeleton.map(
              ({ title, properties, isPastVisitVariant, isIntakeFormVariant, emptyText = '--' }) => (
                <PatientChartsPrintedFriendlySection
                  key={title}
                  title={title}
                  properties={properties}
                  isPastVisitVariant={isPastVisitVariant}
                  isIntakeFormVariant={isIntakeFormVariant}
                  emptyText={emptyText}
                />
              ),
            )}
            <Text
              style={styles.pageNumberText}
              render={({ pageNumber, totalPages }) =>
                pageNumber === totalPages ? '' : `Patient records page ${pageNumber} of ${totalPages}`
              }
              fixed
            />
            <PatientChartsPrintedFriendlyFooter />
          </Page>
        </Document>
      </PDFViewer>
    </div>
  )
}
export default PatientChartsPrintedFriendly
